'use client'

import { Card, Cell, Spacer, Text } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { ErrorState } from '@marketplace-web/error-display/error-display-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  getProTermsAndConditionsContent,
  transformBusinessAccountsTermsOfUseResponse,
} from '@marketplace-web/business-accounts/business-accounts-data'

type Props = {
  isPreviousVersion?: boolean
}

const ProTermsAndConditions = ({ isPreviousVersion }: Props) => {
  const {
    fetch: fetchProTermsAndConditions,
    transformedData: termsOfUseContent,
    isLoading,
    error,
  } = useFetch(getProTermsAndConditionsContent, transformBusinessAccountsTermsOfUseResponse)
  const breakpoints = useBreakpoint()

  useEffect(() => {
    fetchProTermsAndConditions({ isPreviousVersion })
  }, [fetchProTermsAndConditions, isPreviousVersion])

  const renderBannerContent = (termsUpdates: { title: string; body: string }) => {
    return (
      <div className="u-ui-padding-x2-large@desktops">
        <Text as="h1" type="heading">
          {termsUpdates.title}
        </Text>
        <div className="u-desktops-only">
          <Spacer size="x-large" />
        </div>
        <div className="u-mobiles-only">
          <Spacer size="medium" />
        </div>
        <div>
          <Text as="span" html>
            {termsUpdates.body}
          </Text>
        </div>
      </div>
    )
  }

  const renderBannerBasedOnBreakpoint = () => {
    if (!termsOfUseContent?.terms_updates) return null

    const { terms_updates } = termsOfUseContent

    if (breakpoints.phones) {
      return (
        <div>
          <Spacer size="x-large" />
          <Card testId="pro-terms-and-conditions-banner">{renderBannerContent(terms_updates)}</Card>
          <Spacer size="x4-large" />
        </div>
      )
    }

    return (
      <div>
        <Spacer size="x2-large" />
        <Card styling="elevated" testId="pro-terms-and-conditions-banner">
          {renderBannerContent(terms_updates)}
        </Card>
        <Spacer size="x4-large" />
      </div>
    )
  }

  if (error) return <ErrorState />
  if (isLoading || !termsOfUseContent)
    return (
      <ContentLoader
        styling={ContentLoader.Styling.Wide}
        testId="pro-terms-and-conditions-loader"
      />
    )

  return (
    <Card>
      <Cell styling="wide">
        {renderBannerBasedOnBreakpoint()}
        <Text as="h1" type="section-heading">
          {termsOfUseContent.title}
        </Text>
        <Spacer size="x-large" />
        <div className="user-created-content">
          <Text as="span" type="body" html>
            {termsOfUseContent.body}
          </Text>
        </div>
      </Cell>
    </Card>
  )
}

export default ProTermsAndConditions
